import { ApiError, api, dateiSenden } from "./api";
import { imRegal, REGAL_AUFTRAEGE } from "./datenbank";

/**
 * Die Warteschlange fuer Arbeiten ohne Netz.
 *
 * Am Objekt gibt es Funkloecher. Der Plan sieht deshalb ausdruecklich kein
 * vollstaendiges Abgleichsystem vor, sondern genau dies: was der Mitarbeiter
 * unterwegs tut, landet auf dem Geraet und geht bei Netz von selbst raus.
 *
 * Vier Dinge sollen ohne Netz gehen: Checkliste ausfuellen, fotografieren,
 * Arbeitszeit stempeln, Auftrag zurueckmelden. Alles andere verlangt Netz und
 * sagt das auch. Diese Abgrenzung spart Wochen Arbeit und deckt den Fall ab.
 *
 * **Warum IndexedDB und nicht localStorage.** Fotos sind Binaerdaten von
 * mehreren Megabyte. localStorage kann nur Text und ist auf wenige Megabyte
 * begrenzt; ein Foto muesste als Base64 abgelegt werden und waere danach ein
 * Drittel groesser. IndexedDB nimmt Blobs, wie sie sind.
 *
 * **Warum strikt der Reihe nach.** Ein Foto gehoert zu einem Pruefpunkt, eine
 * Einreichung setzt beides voraus. Wuerde die Schlange die Reihenfolge
 * aufgeben, koennte eine Einreichung vor ihren Fotos ankommen und der Server
 * wiese sie als unvollstaendig zurueck, obwohl alles da ist.
 *
 * **Warum ein Vorgangsschluessel.** Ein Telefon bekommt die Antwort auf eine
 * Anfrage nicht immer mit. Ohne Schluessel legte der zweite Versuch eine
 * zweite Buchung an. Der Server erkennt den Schluessel wieder und antwortet
 * wie beim ersten Mal.
 *
 * **Warum nichts still verschwindet.** Wird eine Anfrage vom Server dauerhaft
 * abgelehnt, etwa weil der Bericht inzwischen freigegeben ist, bleibt der
 * Eintrag mit seinem Grund stehen und ist sichtbar. Ein stiller Verlust waere
 * das Schlimmste, was diese Schlange tun koennte: der Mitarbeiter glaubt, er
 * habe abgegeben, und niemand merkt etwas.
 */

export type Art = "pruefpunkt" | "foto" | "stempel" | "auftrag" | "einreichen";

export interface Auftrag {
  art: Art;
  /** Pfad ohne /api/v1, wie bei api.get. */
  pfad: string;
  methode: "POST" | "PATCH" | "PUT";
  /** Rumpf als JSON. Entweder das oder eine Datei, nie beides. */
  koerper?: unknown;
  /**
   * Eine Datei mit den Feldern, die im selben Formular mitgehen.
   *
   * feldname ist der Name des Dateifeldes. Er ist nicht ueberall gleich: der
   * Bericht erwartet "datei", der Auftrag "file". Wer ihn fest verdrahtet,
   * bekommt an einer der beiden Stellen einen Upload, den der Server nicht
   * liest, und zwar erst dann, wenn die Schlange abgearbeitet wird.
   */
  datei?: { blob: Blob; name: string; feldname?: string; felder?: Record<string, string> };
  /** Was in der Anzeige steht, in der Sprache des Nutzers. */
  beschreibung: string;
  /**
   * Statuscodes, die bedeuten "der Server hat das schon". Beim Einreichen ist
   * das 409: der Bericht ist bereits eingereicht, also war der erste Versuch
   * erfolgreich und nur die Antwort ging verloren.
   */
  erledigtBei?: number[];
}

export interface Eintrag extends Auftrag {
  id: string;
  angelegt: string;
  versuche: number;
  /** Gesetzt, wenn der Server dauerhaft ablehnt. Dann wartet der Eintrag auf einen Menschen. */
  fehler: string | null;
}

export interface Stand {
  offen: number;
  fehlerhaft: number;
  laeuft: boolean;
  online: boolean;
}

/* ------------------------------------------------------------------ Ablage */

/**
 * Die Persistenz steckt hinter dieser kleinen Schnittstelle, damit die Tests
 * ohne Browser laufen. Sie kennen nur die Reihenfolge und die Fehlerbehandlung,
 * und genau die will man pruefen.
 */
export interface Speicher {
  alle(): Promise<Eintrag[]>;
  legen(e: Eintrag): Promise<void>;
  entfernen(id: string): Promise<void>;
}

function indexedDbSpeicher(): Speicher {
  return {
    async alle() {
      const gefunden = await imRegal<Eintrag[]>(REGAL_AUFTRAEGE, "readonly", (r) => r.getAll());
      // Nach Anlage sortiert: die Schlange haelt ihre Reihenfolge auch ueber
      // einen Neustart des Geraets hinweg.
      return gefunden.sort((a, b) => a.angelegt.localeCompare(b.angelegt));
    },
    async legen(e) {
      await imRegal(REGAL_AUFTRAEGE, "readwrite", (r) => r.put(e));
    },
    async entfernen(id) {
      await imRegal(REGAL_AUFTRAEGE, "readwrite", (r) => r.delete(id));
    }
  };
}

let speicher: Speicher | null = null;

/** Setzt die Ablage. Ohne Aufruf wird IndexedDB benutzt. Die Tests setzen eine eigene. */
export function speicherSetzen(eigen: Speicher | null): void {
  speicher = eigen;
}

function ablage(): Speicher {
  if (!speicher) speicher = indexedDbSpeicher();
  return speicher;
}

/* ------------------------------------------------------------------ Senden */

export type Sender = (e: Eintrag) => Promise<void>;

const echterSender: Sender = async (e) => {
  if (e.datei) {
    const form = new FormData();
    for (const [name, wert] of Object.entries(e.datei.felder ?? {})) form.append(name, wert);
    form.append(e.datei.feldname ?? "datei", e.datei.blob, e.datei.name);
    await dateiSenden(e.pfad, form);
    return;
  }
  if (e.methode === "PATCH") await api.patch(e.pfad, e.koerper ?? {});
  else if (e.methode === "PUT") await api.put(e.pfad, e.koerper ?? {});
  else await api.post(e.pfad, e.koerper);
};

let sender: Sender = echterSender;

/** Ersetzt den Versand. Nur fuer Tests. */
export function senderSetzen(eigen: Sender | null): void {
  sender = eigen ?? echterSender;
}

/* ------------------------------------------------------------- Beobachtung */

const zuhoerer = new Set<(s: Stand) => void>();
let laeuft = false;
let laufend: Promise<Bilanz> | null = null;

export function beobachten(rueckruf: (s: Stand) => void): () => void {
  zuhoerer.add(rueckruf);
  void melden();
  return () => zuhoerer.delete(rueckruf);
}

async function melden(): Promise<void> {
  if (zuhoerer.size === 0) return;
  const alle = await ablage().alle();
  const stand: Stand = {
    offen: alle.filter((e) => !e.fehler).length,
    fehlerhaft: alle.filter((e) => e.fehler).length,
    laeuft,
    online: typeof navigator === "undefined" ? true : navigator.onLine
  };
  for (const z of zuhoerer) z(stand);
}

/* -------------------------------------------------------------- Schlange */

let zaehler = 0;

function kennung(): string {
  // crypto.randomUUID gibt es in jedem Browser, den wir unterstuetzen, und in
  // Node ab 19. Ein eigener Zufallsgenerator waere hier nur eine Fehlerquelle.
  return crypto.randomUUID();
}

/**
 * Einen Auftrag in die Schlange stellen und sofort einen Versuch anstossen.
 *
 * Der Aufrufer wartet nicht auf den Versand. Genau das ist der Sinn: die
 * Oberflaeche darf nicht an einer Funkluecke haengen bleiben.
 */
export async function einreihen(auftrag: Auftrag): Promise<Eintrag> {
  const eintrag: Eintrag = {
    ...auftrag,
    id: kennung(),
    /*
     * Zeitstempel und ein laufender Zaehler.
     *
     * Der Zaehler ist nicht schmueckendes Beiwerk: zwei Eintraege koennen in
     * derselben Millisekunde entstehen, etwa ein Pruefpunkt und sein Foto.
     * Zuerst stand hier ein Zufallswert, und die Reihenfolge kippte damit
     * zufaellig um. Der Test hat es gefunden, im Betrieb waere es eine
     * Einreichung vor ihren Fotos gewesen.
     *
     * Ueber einen Neustart hinweg traegt der Zeitstempel, denn ein Neustart
     * dauert laenger als eine Millisekunde.
     */
    angelegt: `${new Date().toISOString()}-${String((zaehler += 1)).padStart(6, "0")}`,
    versuche: 0,
    fehler: null
  };
  await ablage().legen(eintrag);
  await melden();
  void abarbeiten();
  return eintrag;
}

/**
 * War das nur das Netz?
 *
 * Ein Netzfehler und ein ueberlasteter Server sind voruebergehend, ein "nein"
 * des Servers ist es nicht. Diese Unterscheidung entscheidet ueberall darueber,
 * ob etwas in die Warteschlange geht oder als Fehler auf dem Bildschirm steht.
 */
export function nurEinNetzproblem(err: unknown): boolean {
  if (err instanceof ApiError) return err.status >= 500 || err.status === 429;
  return true;
}

/**
 * Erst senden, und wenn das am Netz scheitert, einreihen.
 *
 * Der Weg fuer jede Eingabe, die ohne Netz funktionieren soll. Zurueck kommt,
 * was passiert ist, damit die Oberflaeche den richtigen Satz schreiben kann:
 * "gespeichert" oder "ohne Netz gespeichert, geht spaeter raus".
 *
 * Bei 401 wird ebenfalls eingereiht. Die Sitzung ist abgelaufen, api.ts hat
 * die Erneuerung schon versucht; die Eingabe selbst ist in Ordnung und darf
 * nicht verloren gehen, nur weil jemand zu lange nichts getan hat.
 */
export async function sendenOderEinreihen(
  auftrag: Auftrag,
  direkt: () => Promise<void>
): Promise<"gesendet" | "eingereiht"> {
  try {
    await direkt();
    return "gesendet";
  } catch (err) {
    const abgelaufen = err instanceof ApiError && err.status === 401;
    if (!nurEinNetzproblem(err) && !abgelaufen) throw err;
    await einreihen(auftrag);
    return "eingereiht";
  }
}

export function eintraege(): Promise<Eintrag[]> {
  return ablage().alle();
}

/** Einen dauerhaft abgelehnten Eintrag verwerfen. Nur auf ausdruecklichen Wunsch. */
export async function verwerfen(id: string): Promise<void> {
  await ablage().entfernen(id);
  await melden();
}

/** Einen abgelehnten Eintrag noch einmal versuchen, etwa nachdem der Grund behoben ist. */
export async function nochmal(id: string): Promise<void> {
  const alle = await ablage().alle();
  const e = alle.find((x) => x.id === id);
  if (!e) return;
  await ablage().legen({ ...e, fehler: null });
  await melden();
  void abarbeiten();
}

interface Bilanz {
  gesendet: number;
  offen: number;
  fehlerhaft: number;
}

/**
 * Die Schlange abarbeiten.
 *
 * Der Reihe nach und mit einer klaren Unterscheidung:
 *
 *   Netzfehler, 5xx, 429, 401   Es liegt nicht am Eintrag. Abbrechen und
 *                               spaeter alles noch einmal versuchen.
 *   4xx                         Es liegt am Eintrag. Ihn kennzeichnen und mit
 *                               dem naechsten weitermachen, sonst blockiert
 *                               ein einziger kaputter Eintrag alle anderen.
 *
 * Bei 401 wird ausdruecklich abgebrochen statt gekennzeichnet: die Sitzung ist
 * abgelaufen, das hat nichts mit dem Eintrag zu tun, und nach der naechsten
 * Anmeldung geht er ohne Zutun raus.
 */
export function abarbeiten(): Promise<Bilanz> {
  /*
   * Laeuft schon einer, bekommt der Aufrufer genau dessen Zusage zurueck.
   *
   * Frueher stand hier eine leere Bilanz. Das war stiller Unsinn: wer
   * abarbeiten() aufrief, waehrend ein Durchgang lief, bekam "nichts
   * gesendet" gemeldet, obwohl gerade gesendet wurde. In den Tests fiel es
   * sofort auf, im Betrieb waere es eine Anzeige gewesen, die manchmal luegt.
   */
  if (!laufend) laufend = durchgang().finally(() => { laufend = null; });
  return laufend;
}

async function durchgang(): Promise<Bilanz> {
  laeuft = true;
  await melden();

  let gesendet = 0;
  try {
    /*
     * Wiederholt, solange etwas rausging. Waehrend eines Durchgangs kann ein
     * neuer Auftrag dazukommen, etwa das Foto zum Pruefpunkt, der gerade
     * gesendet wurde. Ohne diese Schleife bliebe er bis zum naechsten Wecker
     * liegen, und das sind zwei Minuten.
     */
    for (;;) {
      let inDiesemLauf = 0;
      let abgebrochen = false;

      for (const e of await ablage().alle()) {
        if (e.fehler) continue;
        try {
          await sender(e);
          await ablage().entfernen(e.id);
          gesendet += 1;
          inDiesemLauf += 1;
          continue;
        } catch (err) {
          const status = err instanceof ApiError ? err.status : 0;

          if (status && (e.erledigtBei ?? []).includes(status)) {
            // Der Server hat das schon. Kein Fehler, sondern dasselbe Ergebnis
            // wie beim ersten Mal.
            await ablage().entfernen(e.id);
            gesendet += 1;
            inDiesemLauf += 1;
            continue;
          }

          if (status >= 400 && status < 500 && status !== 401 && status !== 429) {
            await ablage().legen({
              ...e,
              versuche: e.versuche + 1,
              fehler: err instanceof ApiError ? err.message : "Abgelehnt"
            });
            continue;
          }

          // Alles andere: Netz weg, Server ueberlastet, Sitzung abgelaufen.
          await ablage().legen({ ...e, versuche: e.versuche + 1 });
          abgebrochen = true;
          break;
        }
      }

      if (abgebrochen || inDiesemLauf === 0) break;
    }
  } finally {
    laeuft = false;
  }

  const rest = await ablage().alle();
  await melden();
  return {
    gesendet,
    offen: rest.filter((x) => !x.fehler).length,
    fehlerhaft: rest.filter((x) => x.fehler).length
  };
}

let wecker: ReturnType<typeof setInterval> | null = null;

/**
 * Die Schlange in Betrieb nehmen.
 *
 * Drei Anlaesse: das Netz kommt zurueck, die Seite wird wieder sichtbar, und
 * alle zwei Minuten. Der letzte ist der wichtigste, denn "online" heisst im
 * Browser nur "es gibt eine Verbindung", nicht "sie traegt". Wer in Mallorca
 * hinter dem Berg steht, hat oft beides gleichzeitig.
 */
export function warteschlangeStarten(): void {
  if (wecker) return;
  window.addEventListener("online", () => void abarbeiten());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void abarbeiten();
  });
  wecker = setInterval(() => void abarbeiten(), 120_000);
  void abarbeiten();
}
